import React, { FunctionComponent, memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SurveyContext } from '@context/SurveyContext'
import { ConsentContext } from '@context/ConsentContext'
import { AnswerObject, OneOfManyQuestion, QuestionType, Survey, questionHasChoiceList } from '@models/Survey'
import { Choice, FlatQuestion, Graphic, GraphicMeta, Question } from '@models/Survey/Survey'
import {
  defaultAlbumImageBox,
  defaultBookImageBox,
  defaultCountryImageBox,
  defaultMovieImageBox,
} from '@models/ImageComposer'
import { AlbumEntry } from '@models/Music'
import { MovieEntry } from '@models/Movie'
import { CountryEntry } from '@models/Country'

import { BookEntry } from '@models/Book'
import axios from 'axios'
import debounce from 'lodash/debounce'
import { resolveCdnMediaUrl, rewriteFlagAssetUrl } from '@helpers/cdnMediaUrl'
import { useShare } from '@hooks/useShare'
import { ImageLoadingOverlay } from './ImageLoadingOverlay'
import { pickAnswerSlotGraphic, resolveFramedArtworkMeta } from './answerSlotGraphic'

interface Props {
  currentQuestion?: Question
  width: number
  height: number
  animate?: boolean
}

interface IdentifiableGraphic extends Graphic {
  id: string
}

const QUEBEC_FLAG_NAME = 'ca-qc'

const mapGraphics = (graphics: Graphic[], typePrefix: string): IdentifiableGraphic[] => {
  return graphics.map((g, i) => {
    const suffixId = (g as IdentifiableGraphic)?.id ?? ''
    return {
      ...g,
      id: `${typePrefix}_${i}${suffixId}`,
    }
  })
}

const mapSingleAnswerGraphics = (questionId: string, keySuffix: string, graphics: Graphic[]): IdentifiableGraphic[] => {
  return mapGraphics(graphics, `${questionId}_a_${keySuffix}`)
}

const mapManyAnswerGraphics = (questionId: string, graphics: Graphic[], answerId: string): IdentifiableGraphic[] => {
  return mapGraphics(graphics, `${questionId}_a${answerId}`)
}

const mapDefaultGraphics = (questionId: string, graphics: Graphic[]): IdentifiableGraphic[] => {
  return mapGraphics(graphics, `${questionId}_d`)
}

const mapStaticGraphics = (questionId: string, graphics: Graphic[]): IdentifiableGraphic[] => {
  return mapGraphics(graphics, `${questionId}_s`)
}

const mapSurveyGraphics = (graphics: Graphic[]): IdentifiableGraphic[] => {
  return mapGraphics(graphics, 'u')
}

// TODO: Set the graphic for freetext questions. Fallback to default is currently applied.
const getFreeTextGraphic = (): IdentifiableGraphic[] => {
  return []
}

const getConditionalGraphics = (
  answerObject: AnswerObject,
  answer: Choice | undefined,
  oneOfManyQuestion: OneOfManyQuestion,
  questionsList: FlatQuestion[],
) => {
  if (answer && oneOfManyQuestion?.meta?.graphicSuffix && oneOfManyQuestion?.meta?.answerLabels && answer.graphics) {
    const dependentQuestion = questionsList.find(
      (q) => q.key === oneOfManyQuestion.meta?.graphicSuffix,
    ) as OneOfManyQuestion
    if (dependentQuestion && dependentQuestion.meta?.answerLabels) {
      const dependentAnswer = dependentQuestion.choices.find((a) => a.id === answerObject[dependentQuestion.id])
      if (dependentAnswer) {
        const dependentIndex = dependentQuestion.choices.indexOf(dependentAnswer)
        const dependentLabel = dependentQuestion.meta.answerLabels[dependentIndex]
        const currentIndex = oneOfManyQuestion.choices.indexOf(answer)
        const currentLabel = oneOfManyQuestion.meta.answerLabels[currentIndex]
        const graphicLabel = `${oneOfManyQuestion.key}_${currentLabel}_${dependentLabel}.png`

        return oneOfManyQuestion.staticGraphics
          ?.filter((g) => g.meta?.conditional && g.image.includes(graphicLabel))
          ?.map((g) => {
            return {
              ...g,
              id: `_${graphicLabel}`,
            } as IdentifiableGraphic
          })
      }
    }
  }
}

const getOneOfManyGraphic = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  questionsList: FlatQuestion[],
): IdentifiableGraphic[] => {
  const oneOfManyQuestion = question as OneOfManyQuestion
  const answer = oneOfManyQuestion.choices.find((a) => a.id === answerObject[question.id])

  const graphics = getConditionalGraphics(answerObject, answer, oneOfManyQuestion, questionsList) || answer?.graphics

  if (answer && graphics?.length) {
    return mapManyAnswerGraphics(question.id, graphics, answer.id)
  }
  return []
}

const getGraphicFromAnswerString = (
  answer: string,
  question: FlatQuestion,
  defaultImageBox: GraphicMeta,
  keySuffix: string,
): IdentifiableGraphic[] => {
  if (answer) {
    const slotGraphic = pickAnswerSlotGraphic(question.defaultGraphics)
    // Trim defaults for MOV/MUS/COUN often have x/y=0 + null size — never use that meta as-is.
    const meta = resolveFramedArtworkMeta(slotGraphic, defaultImageBox)
    return mapSingleAnswerGraphics(question.id, keySuffix, [
      {
        image: answer,
        meta,
        zIndex: meta.zIndex,
      },
    ])
  }
  return []
}

const getGraphicFromMusicAnswer = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  defaultImageBox: GraphicMeta,
): IdentifiableGraphic[] => {
  const answer = answerObject[question.id] as AlbumEntry
  const artworkUrl = answer?.pixelatedArtworkUrl ?? ''
  return getGraphicFromAnswerString(artworkUrl, question, defaultImageBox, answer?.id)
}

const getGraphicFromMovieAnswer = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  defaultImageBox: GraphicMeta,
): IdentifiableGraphic[] => {
  const answer = answerObject[question.id] as MovieEntry
  const artworkUrl = answer?.pixelatedArtworkUrl ?? ''
  return getGraphicFromAnswerString(artworkUrl, question, defaultImageBox, answer?.id)
}

const getGraphicFromBookAnswer = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  defaultImageBox: GraphicMeta,
): IdentifiableGraphic[] => {
  const answer = answerObject[question.id] as BookEntry
  const artworkUrl = answer?.pixelatedThumbnailUrl ?? ''
  return getGraphicFromAnswerString(artworkUrl, question, defaultImageBox, answer?.id)
}

const getGraphicFromCountryAnswer = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  defaultImageBox: GraphicMeta,
): IdentifiableGraphic[] => {
  const answer = answerObject[question.id] as CountryEntry
  const artworkUrl = answer?.pixelatedArtworkUrl ?? ''
  return getGraphicFromAnswerString(artworkUrl, question, defaultImageBox, answer?.code)
}

const fallbackToDefaultGraphic = (graphics: IdentifiableGraphic[], question: FlatQuestion): IdentifiableGraphic[] => {
  if (!graphics?.length && question?.defaultGraphics?.length) {
    return mapDefaultGraphics(question.id, question.defaultGraphics)
  }
  return graphics
}

const getGraphicForQuestion = (
  answerObject: AnswerObject,
  question: FlatQuestion,
  questionsList: FlatQuestion[],
): IdentifiableGraphic[] => {
  switch (question.type) {
    case QuestionType.FreeText:
      if (questionHasChoiceList(question)) {
        return getOneOfManyGraphic(answerObject, question, questionsList)
      }
      return getFreeTextGraphic()

    case QuestionType.PostalCode:
      return getFreeTextGraphic()

    case QuestionType.OneOfMany:
    case QuestionType.OneOfManyGraphic:
      return getOneOfManyGraphic(answerObject, question, questionsList)

    case QuestionType.Music:
      return getGraphicFromMusicAnswer(answerObject, question, defaultAlbumImageBox)

    case QuestionType.Movie:
      return getGraphicFromMovieAnswer(answerObject, question, defaultMovieImageBox)

    case QuestionType.Book:
      return getGraphicFromBookAnswer(answerObject, question, defaultBookImageBox)

    case QuestionType.Country:
      return getGraphicFromCountryAnswer(answerObject, question, defaultCountryImageBox)
  }
  return []
}

const getGraphicList = (
  answerObject: AnswerObject,
  questionsList: FlatQuestion[],
  currentQuestion?: Question,
): IdentifiableGraphic[] => {
  let graphicList: IdentifiableGraphic[] = []

  for (const questionId in answerObject) {
    const question = questionsList.find((q) => q.id === questionId)
    if (!question) continue
    const graphics = fallbackToDefaultGraphic(getGraphicForQuestion(answerObject, question, questionsList), question)
    if (graphics && graphics?.length) {
      graphicList = graphicList.concat(graphics)
    }
    if (graphicList.length && question.staticGraphics) {
      graphicList = graphicList.concat(
        mapStaticGraphics(
          question.id,
          question.staticGraphics.filter((g) => !g.meta?.conditional),
        ),
      )
    }
  }

  if (currentQuestion && !answerObject[currentQuestion.id] && currentQuestion.defaultGraphics) {
    graphicList = graphicList.concat(mapDefaultGraphics(currentQuestion.id, currentQuestion.defaultGraphics))
  }

  return graphicList
}

const sortByZIndex = (a: Graphic, b: Graphic): number => {
  if (a.zIndex < b.zIndex) return -1
  if (a.zIndex > b.zIndex) return 1
  return 0
}

export const getOrderedGraphics = (
  answerObject: AnswerObject,
  questionsList: FlatQuestion[],
  currentQuestion?: Question,
  survey?: Survey,
  animate?: boolean,
) => {
  const surveyGraphics = survey?.graphics ? mapSurveyGraphics(survey.graphics) : []
  const answerGraphics = getGraphicList(answerObject, questionsList, currentQuestion)
  const orderedGraphics = [...surveyGraphics, ...answerGraphics]

  orderedGraphics.sort(sortByZIndex)

  return orderedGraphics
}

export const ImageComposer: FunctionComponent<Props & { loading: boolean; finishLoading: () => void }> = ({
  currentQuestion,
  finishLoading,
  loading,
  animate,
}) => {
  const { setImageHasBeenSet } = useShare()
  const { answerObject, questionsList, survey, setPortraitForSurvey } = useContext(SurveyContext)
  const { consentData } = useContext(ConsentContext)

  const [b64, setB64] = useState<string | undefined>()
  const finishLoadingRef = useRef(finishLoading)
  finishLoadingRef.current = finishLoading
  const setPortraitRef = useRef(setPortraitForSurvey)
  setPortraitRef.current = setPortraitForSurvey
  const surveyKeyRef = useRef(survey?.key)
  surveyKeyRef.current = survey?.key
  const editionKeyRef = useRef<string | undefined>(survey?.category?.key)
  editionKeyRef.current = survey?.category?.key
  const scopeCodeRef = useRef<string | undefined>(
    survey?.category?.scopeCode ?? survey?.category?.scope_code,
  )
  scopeCodeRef.current = survey?.category?.scopeCode ?? survey?.category?.scope_code
  const visitorIdRef = useRef<string | undefined>(consentData?.visitorId)
  visitorIdRef.current = consentData?.visitorId

  const graphics = useMemo(
    () => getOrderedGraphics(answerObject, questionsList, currentQuestion, survey, animate),
    [answerObject, questionsList, currentQuestion, survey],
  )

  const debouncedGeneratePortrait = useMemo(
    () =>
      debounce((graphics: IdentifiableGraphic[]) => {
        // Copy before mutating image URLs — avoid poisoning memoized graphic objects
        const payload = graphics.map((g) => ({ ...g }))
        for (let i = 0; i < payload.length; i++) {
          const g = payload[i]

          const rewrittenFlag = rewriteFlagAssetUrl(g.image)
          if (rewrittenFlag) {
            // Vite `/src/assets/images/flags/…` or `/flags/…` → CDN `/media/flags/{code}.jpg`
            g.image = rewrittenFlag
          } else if (g.image.startsWith('/') || g.image.includes('://api-django')) {
            if (g.image.match(/.*\/no-album/)) {
              g.image = resolveCdnMediaUrl('/graphics/no-album.jpg')
            } else if (g.image.includes('/media/') || g.image.includes('/graphics/')) {
              // Fichiers Django (MEDIA) / graphics — ne pas confondre avec l’heuristique pays ci‑dessous.
              g.image = resolveCdnMediaUrl(g.image)
            } else {
              // Anciens chemins relatifs type /ca-qc.png → JPEG sous /flags/ sur le CDN
              const split = g.image.split('/')[g.image.split('/').length - 1]
              const splittedBySlash = split.split('-')
              let country = splittedBySlash[0]
              const hasFlagContainingHyphen = split.includes(QUEBEC_FLAG_NAME)
              if (hasFlagContainingHyphen) {
                country += `-${splittedBySlash[1]}`
              }
              const countryNoExt = country.split('.')[0]
              g.image = resolveCdnMediaUrl(`/flags/${countryNoExt}.jpg`)
            }
          }
        }
        const context = {
          editionKey: editionKeyRef.current,
          scopeCode: scopeCodeRef.current,
          surveyKey: surveyKeyRef.current,
          visitorId: visitorIdRef.current,
        }
        axios
          .post(`${import.meta.env.VITE_APP_API_BASE_URL}generate-portrait`, { graphics: payload, context })
          .then((res) => {
            const data = res.data
            if (typeof data === 'string' && data.length > 0) {
              const dataUrl =
                data.startsWith('data:') || data.startsWith('http') || data.startsWith('blob:')
                  ? data
                  : `data:image/png;base64,${data}`
              setB64(dataUrl)
              setImageHasBeenSet?.(true)
              const key = surveyKeyRef.current
              if (key) {
                setPortraitRef.current(key, dataUrl)
              }
            }
          })
          .catch((err) => {
            if (import.meta.env.DEV) {
              // Django local n’expose souvent pas POST /generate-portrait (CORS ou 404).
              console.warn('[ImageComposer] generate-portrait indisponible — portrait composite ignoré.', err)
            }
          })
          .finally(() => {
            finishLoadingRef.current()
          })
      }, 100),
    [setImageHasBeenSet],
  )

  useEffect(() => {
    debouncedGeneratePortrait(graphics)
    return () => {
      debouncedGeneratePortrait.cancel()
    }
  }, [graphics, debouncedGeneratePortrait, consentData?.visitorId, survey?.category?.key, survey?.key])

  return (
    <>
      <ImageLoadingOverlay visible={loading} />
      <img id="portrait" src={b64} width="100%" height="100%" crossOrigin="anonymous" />
    </>
  )
}

export default memo(ImageComposer)
